import React, { useState, useRef } from 'react';
import { View, TouchableOpacity, StyleSheet, ScrollView, Dimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';

export type ClubTabType = 'feed' | 'meetups' | 'members' | 'details' | 'referrals';

interface ClubTabBarProps {
  activeTab: ClubTabType;
  onTabChange: (tab: ClubTabType) => void;
}

const ClubTabBar: React.FC<ClubTabBarProps> = ({ activeTab, onTabChange }) => {
  const { theme } = useTheme();
  const scrollViewRef = useRef<ScrollView>(null);
  const [showRightIndicator, setShowRightIndicator] = useState(true);
  const [contentWidth, setContentWidth] = useState(0);
  const [scrollViewWidth, setScrollViewWidth] = useState(0);

  const tabs: { key: ClubTabType; label: string; icon: string }[] = [
    // { key: 'feed', label: 'Feed', icon: 'newspaper-outline' }, // Hidden - replaced by Referrals
    { key: 'referrals', label: 'Leads', icon: 'git-network-outline' },
    { key: 'meetups', label: 'Meet-ups', icon: 'calendar-outline' },
    { key: 'members', label: 'Members', icon: 'people-outline' },
    { key: 'details', label: 'Details', icon: 'information-circle-outline' },
  ];

  const handleScroll = (event: any) => {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const isCloseToEnd = contentOffset.x + layoutMeasurement.width >= contentSize.width - 10;
    setShowRightIndicator(!isCloseToEnd);
  };

  const handleContentSizeChange = (width: number) => {
    setContentWidth(width);
    setShowRightIndicator(width > scrollViewWidth);
  };

  const handleLayout = (event: any) => {
    const { width } = event.nativeEvent.layout;
    setScrollViewWidth(width);
    setShowRightIndicator(contentWidth > width);
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.background, borderBottomColor: theme.border }]}>
      <ScrollView 
        ref={scrollViewRef}
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        onContentSizeChange={handleContentSizeChange}
        onLayout={handleLayout}
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <TouchableOpacity
              key={tab.key}
              style={[
                styles.tab,
                isActive && { borderBottomColor: theme.accent, borderBottomWidth: 2 }
              ]}
              onPress={() => onTabChange(tab.key)}
              activeOpacity={0.7}
            >
              <Ionicons 
                name={tab.icon as any} 
                size={20} 
                color={isActive ? theme.accent : theme.textSecondary} 
              />
              <ThemedText
                style={[
                  styles.tabLabel,
                  { color: isActive ? theme.accent : theme.textSecondary },
                  isActive && styles.tabLabelActive
                ]}
              >
                {tab.label}
              </ThemedText>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
      
      {/* Right fade indicator to show more content */}
      {showRightIndicator && (
        <LinearGradient
          colors={[
            `${theme.background}00`,
            `${theme.background}99`,
            theme.background,
          ]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={styles.fadeIndicator}
          pointerEvents="none"
        >
          <View style={styles.chevronContainer}>
            <Ionicons 
              name="chevron-forward" 
              size={18} 
              color={theme.textSecondary} 
            />
          </View>
        </LinearGradient>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderBottomWidth: 1,
    position: 'relative',
  },
  scrollContent: {
    paddingHorizontal: 8,
    paddingRight: 40, // Add extra padding to ensure last tab is fully visible
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    gap: 6,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabLabel: {
    fontSize: 15,
    fontWeight: '500',
  },
  tabLabelActive: {
    fontWeight: '700',
  },
  fadeIndicator: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 1,
    width: 60,
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  chevronContainer: {
    paddingRight: 8,
  },
});

export default ClubTabBar;
